import React from "react";
import {
  Box,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  useTheme,
} from "@mui/material";
import {
  CheckCircle as CheckCircleIcon,
} from "@mui/icons-material";
import PageContainer from "../../components/PageContainer";
import PageSection from "../../components/PageSection";
import { knowledgeItems } from "../../data/products";
import { lightTheme } from "../../styles/theme";

const NoticesHelp: React.FC = () => {
  const theme = useTheme();
  const noticesData = knowledgeItems.find((item) => item.id === "notices");

  if (!noticesData) {
    return (
      <PageContainer>
        <Typography>Help content not found</Typography>
      </PageContainer>
    );
  }

  const renderContent = (content: any): React.ReactElement | null => {
    if (!content) return null;

    if (typeof content === "string") {
      return (
        <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.6 }}>
          {content}
        </Typography>
      );
    }

    if (Array.isArray(content)) {
      return (
        <List dense sx={{ mb: 2 }}>
          {content.map((item, index) => {
            if (
              typeof item === "object" &&
              item !== null &&
              "type" in item &&
              "description" in item
            ) {
              return (
                <ListItem
                  key={index}
                  sx={{
                    py: 1,
                    px: 0,
                    flexDirection: "column",
                    alignItems: "flex-start",
                  }}
                >
                  <Box sx={{ width: "100%", mb: 1 }}>
                    <Typography
                      variant="h6"
                      fontWeight={600}
                      sx={{ mb: 0.5, color: "primary.main" }}
                    >
                      {item.type}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: 1, lineHeight: 1.5 }}
                    >
                      {item.description}
                    </Typography>
                    {item.features && Array.isArray(item.features) && (
                      <Box sx={{ ml: 1 }}>
                        {item.features.map(
                          (feature: string, featureIndex: number) => (
                            <Typography
                              key={featureIndex}
                              variant="body2"
                              color="text.secondary"
                              sx={{
                                fontSize: "0.8rem",
                                mb: 0.5,
                                display: "flex",
                                alignItems: "center",
                              }}
                            >
                              <CheckCircleIcon
                                fontSize="small"
                                color="primary"
                                sx={{ mr: 1, fontSize: "1rem" }}
                              />
                              {feature}
                            </Typography>
                          )
                        )}
                      </Box>
                    )}
                    {item.steps && Array.isArray(item.steps) && (
                      <Box sx={{ ml: 1, mt: 1 }}>
                        <Typography
                          variant="caption"
                          fontWeight={600}
                          sx={{
                            color: "primary.main",
                            mb: 0.5,
                            display: "block",
                          }}
                        >
                          Steps:
                        </Typography>
                        {item.steps.map((step: string, stepIndex: number) => (
                          <Typography
                            key={stepIndex}
                            variant="body2"
                            color="text.secondary"
                            sx={{
                              fontSize: "0.8rem",
                              mb: 0.5,
                              display: "flex",
                              alignItems: "center",
                            }}
                          >
                            <CheckCircleIcon
                              fontSize="small"
                              color="primary"
                              sx={{ mr: 1, fontSize: "1rem" }}
                            />
                            {step}
                          </Typography>
                        ))}
                      </Box>
                    )}
                  </Box>
                </ListItem>
              );
            }

            return (
              <ListItem key={index} sx={{ py: 0.5, px: 0 }}>
                <ListItemIcon sx={{ minWidth: 32 }}>
                  <CheckCircleIcon fontSize="small" color="primary" />
                </ListItemIcon>
                <ListItemText
                  primary={
                    typeof item === "string" ? item : JSON.stringify(item)
                  }
                  sx={{
                    "& .MuiListItemText-primary": { fontSize: (theme) => theme.typography.body2.fontSize },
                  }}
                />
              </ListItem>
            );
          })}
        </List>
      );
    }

    if (typeof content === "object" && content !== null) {
      return (
        <Box>
          {Object.entries(content).map(([key, value]) => {
            if (!value || value === "") return null;

            const displayKey = key
              .replace(/([A-Z])/g, " $1")
              .replace(/^./, (str) => str.toUpperCase())
              .trim();

            return (
              <Box key={key} sx={{ mb: 3 }}>
                <Typography
                  variant="h6"
                  fontWeight={600}
                  sx={{
                    mb: 1.5,
                    color: "primary.main",
                    fontSize: "1rem",
                  }}
                >
                  {displayKey}
                </Typography>
                {renderContent(value)}
              </Box>
            );
          })}
        </Box>
      );
    }

    return null;
  };

  return (
    <PageContainer>
      {/* Header */}
      <PageSection sx={{ mb: 4 }}>

        <Paper
          elevation={0}
          sx={{
            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${lightTheme.colors.primaryAltDark} 100%)`,
            borderRadius: 3,
            p: { xs: 4, md: 6 },
            color: "common.white",
            textAlign: "center",
          }}
        >
          <Typography
            variant="h3"
            sx={{
              fontWeight: 700,
              mb: 2,
              fontSize: { xs: "2rem", md: "2.5rem" },
            }}
          >
            {noticesData.title}
          </Typography>
          <Typography
            variant="h6"
            sx={{
              opacity: 0.9,
              fontSize: { xs: "1rem", md: "1.125rem" },
              maxWidth: "800px",
              mx: "auto",
              lineHeight: 1.6,
              fontWeight: 400,
            }}
          >
            {noticesData.description}
          </Typography>

          {/* Tags */}
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 1,
              justifyContent: "center",
              mt: 3,
            }}
          >
            {noticesData.tags.map((tag) => (
              <Chip
                key={tag}
                label={tag}
                size="small"
                variant="outlined"
                sx={{
                  color: "common.white",
                  borderColor: theme.palette.whiteOverlays.overlay30,
                  "&:hover": {
                    borderColor: "white",
                  },
                }}
              />
            ))}
          </Box>
        </Paper>
      </PageSection>

      {/* Content */}
      <PageSection>
        <Paper
          elevation={0}
          sx={{
            p: 4,
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 3,
            bgcolor: "card",
          }}
        >
          {renderContent(noticesData.content)}
        </Paper>
      </PageSection>
    </PageContainer>
  );
};

export default NoticesHelp;
